import { Dispatch, useEffect, useRef, useState } from 'react';
import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays';
import { connect, useDispatch } from 'react-redux';
import { FormSpy } from 'react-final-form';

import { ORGANIZATION_FOR_SELECT } from 'CorePackage/MainModule/routes/routes';
import { IKnmErknmFormSubject } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormSubject';
import { getKnmFormRef } from 'KnmPackage/MainModule/selectors/knmSelectors';
import { KnmCollapse } from 'KnmPackage/MainModule/components/KnmCollapse';
import { getOrganizationBlockId } from 'KnmErknmPackage/MainModule/constants/knmErknmFieldsElementsIds';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { knmPmSetChoosenOrganizationsByOtherPassport } from 'KnmPmPackage/MainModule/actions/knmPmActions';

import { ErknmSubjectItem } from './ErknmSubjectItem';
import styles from './ErknmSubjectSelectInPm.module.scss';
import { checkHasErrorInCollapseBlock } from '../../../functions/checkHasErrorInCollapseBlock';

interface IErknmSubjectSelectInPmProps {
  organizationsForSelect: IKnmErknmFormSubject[] | undefined;
}

interface IErknmSubjectStoreProps {
  formRef: IKnmFormRef;
}

const ErknmSubjectSelectInPmWithoutStore = ({
  formRef,
  organizationsForSelect,
}: IErknmSubjectSelectInPmProps & IErknmSubjectStoreProps) => {
  const [knmCollapseOpened, setKnmCollapseOpened] = useState<{ [key: string]: boolean }>({});
  const isMultiFieldsByVisibleForRepeatCheck = useRef((organizationsForSelect?.length || 0) > 1);
  const remove = formRef?.current.mutators.remove;
  const dispatch = useDispatch<Dispatch<RootActions>>();

  useEffect(() => {
    isMultiFieldsByVisibleForRepeatCheck.current = (organizationsForSelect?.length || 0) > 1;
  }, [organizationsForSelect]);

  const makeRemoveErknmSubjectItemHandler = (index: number) => () => {
    if (organizationsForSelect) {
      if (organizationsForSelect.length > 2) {
        remove && remove('organizationsForSelect', index);
      } else {
        const selectedOrg = organizationsForSelect.find((_item, i) => i !== index);

        if (selectedOrg) {
          dispatch(knmPmSetChoosenOrganizationsByOtherPassport(selectedOrg));
          formRef?.current.change('organizationsForSelect');
        }
      }
    }
  };

  return (
    <>
      <FieldBlock className={styles.Wrapper} label={'Сведения о контролируемом лице'} id={ORGANIZATION_FOR_SELECT}>
        <FieldArray
          name="organizationsForSelect"
          key={1}
          validate={() => {
            if (isMultiFieldsByVisibleForRepeatCheck.current) {
              return 'Может быть указано только одно контролируемое лицо';
            }

            return undefined;
          }}
        >
          {({ fields }: FieldArrayRenderProps<IKnmErknmFormSubject, HTMLElement>) => {
            if (!fields || !fields.length || fields.length === 0) {
              return null;
            }

            return (
              <>
                <FormSpy subscription={{ errors: true, touched: true }}>
                  {props => {
                    if (props.errors?.organizationsForSelect) {
                      const fieldError = props.errors?.organizationsForSelect['FINAL_FORM/array-error'];
                      const isFieldTouched = props.touched?.organizationsForSelect;

                      if (isFieldTouched && fieldError && typeof fieldError === 'string') {
                        return <p className="shared-input-error">{fieldError}</p>;
                      }
                    }

                    return null;
                  }}
                </FormSpy>
                {fields.map((name, index) => {
                  const fieldValue: IKnmErknmFormSubject = fields.value?.[index];
                  const id = getOrganizationBlockId(fieldValue.listId);
                  const isNewObject = fieldValue.isNew;

                  if (!fieldValue.isNew && fieldValue.isEmpty) {
                    return null;
                  }

                  const hasError = checkHasErrorInCollapseBlock(formRef, name);

                  return (
                    <KnmCollapse
                      id={id}
                      theme="fields"
                      title={`Контролируемое лицо №${index + 1}`}
                      isNew={isNewObject}
                      opened={isNewObject}
                      onClose={
                        isMultiFieldsByVisibleForRepeatCheck.current
                          ? makeRemoveErknmSubjectItemHandler(index)
                          : undefined
                      }
                      key={name}
                      changeOpenedCallback={e =>
                        setKnmCollapseOpened(state => ({
                          ...state,
                          [name]: e,
                        }))
                      }
                      error={hasError && knmCollapseOpened[name]}
                    >
                      <ErknmSubjectItem
                        id={id}
                        name={name}
                        watchOnlyExtended={true}
                        disableFieldByCompleteStatuses={false}
                        disableFieldByInProcessStatuses={false}
                        isConnectedToErvkObject={false}
                        isCollapsing={true}
                        okveds={fieldValue.okveds}
                        inn={fieldValue.inn}
                        ogrn={fieldValue.ogrn}
                        firstOrganizationName={fieldValue.firstOrganizationName}
                        inspectionResults={fieldValue.inspectionResults}
                        checkSubjectResult={fieldValue.checkSubjectResult}
                        isAutocomplete={fieldValue.isAutocomplete}
                        isMultiFields={true}
                        mspCode={fieldValue.mspCode}
                        mspRegistryDate={fieldValue.mspRegistryDate}
                        isNew={!!fieldValue.isNew}
                        subjectType={fieldValue.subjectType}
                        kpp={fieldValue.kpp}
                        smevRequestStatus={fieldValue.smevRequestStatus}
                        rafpNza={fieldValue.rafpNza}
                        isSonko={fieldValue.isSonko}
                        snils={fieldValue.snils}
                        ervkId={fieldValue.ervkId}
                        isPresentInItRegistry={fieldValue.isPresentInItRegistry}
                        itAccreditationResponse={fieldValue.itAccreditationResponse}
                        smevItAccreditationRequestStatus={fieldValue.smevItAccreditationRequestStatus}
                        organizationsForSelect={organizationsForSelect}
                        isFilledFromLink={fieldValue.isFilledFromLink === true}
                      />
                    </KnmCollapse>
                  );
                })}
              </>
            );
          }}
        </FieldArray>
      </FieldBlock>
    </>
  );
};

const mapStateToProps = (state: IRootState) => ({
  formRef: getKnmFormRef(state),
});

export const ErknmSubjectSelectInPm = connect(mapStateToProps)(ErknmSubjectSelectInPmWithoutStore);
